import Microgame from '../engine/Microgame.js';

export default class SquashGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Écrase !`, en: `Squash!`, es: `¡Aplastad!` },
            controls: { fr: `Tape au pif très vite sur toutes les cibles`, en: `Tap randomly and quickly on all targets`, es: `Toca al azar y rápidamente en todos los objetivos` },
            objective: { fr: `Élimine tout ce qui apparaît à l'écran`, en: `Eliminate everything that appears`, es: `Elimina todo lo que aparezca` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" stroke-width="2"><path d="M3 3h18v18H3z"/><path d="m8 8 8 8"/><path d="m16 8-8 8"/></svg>`,
            actions: [
                { type: 'wait', duration: 320 },
                { type: 'tapTarget', selector: 'div[data-alive="true"]', duration: 150 },
                { type: 'wait', duration: 140 },
                { type: 'tapTarget', selector: 'div[data-alive="true"]', duration: 150 },
                { type: 'wait', duration: 140 },
                { type: 'tapTarget', selector: 'div[data-alive="true"]', duration: 150 },
                { type: 'wait', duration: 140 },
                { type: 'tapTarget', selector: 'div[data-alive="true"]', duration: 150 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.spiders = [];
        const speedMult = engine.speedMultiplier || 1.0;
        // 6 bugs at x1, scaling down to 4 at x4
        this.spiderCount = Math.round(6 - (speedMult - 1) * (2 / 3));
        this.squashedCount = 0;

        // Give a LOT more time: base 4500ms, and very soft scaling
        const adjustedSpeed = 1 + (speedMult - 1) * (2.2 / 3);
        this.duration = 4500 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "SQUASH!";

        for (let i = 0; i < this.spiderCount; i++) {
            this.createSpider();
        }

        this.onDown = this.onDown.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
    }

    createSpider() {
        const spider = document.createElement('div');
        spider.style.position = 'absolute';
        spider.style.width = '50px';
        spider.style.height = '50px';
        spider.style.background = this.secondaryColor;
        spider.style.borderRadius = '50%';
        spider.style.boxShadow = `0 0 10px ${this.secondaryColor}`;

        // Random pos
        const x = 50 + Math.random() * (this.container.clientWidth - 100);
        const y = 50 + Math.random() * (this.container.clientHeight - 100);
        spider.style.left = `${x}px`;
        spider.style.top = `${y}px`;
        spider.style.transform = 'translate(-50%, -50%)';

        // Legs
        for (let j = 0; j < 8; j++) {
            const leg = document.createElement('div');
            leg.style.position = 'absolute';
            leg.style.width = '15px';
            leg.style.height = '2px';
            leg.style.background = this.secondaryColor;
            leg.style.top = '50%';
            leg.style.left = '50%';
            leg.style.transformOrigin = '0 50%';
            leg.style.transform = `rotate(${j * 45}deg) translateX(20px)`;
            spider.appendChild(leg);
        }

        // Movement data
        spider.dataset.vx = (Math.random() - 0.5) * 300;
        spider.dataset.vy = (Math.random() - 0.5) * 300;
        spider.dataset.alive = 'true';

        this.container.appendChild(spider);
        this.spiders.push(spider);
    }

    update(dt) {
        if (!this.active || this.won) return;

        this.spiders.forEach(spider => {
            if (spider.dataset.alive !== 'true') return;

            let x = parseFloat(spider.style.left);
            let y = parseFloat(spider.style.top);
            let vx = parseFloat(spider.dataset.vx);
            let vy = parseFloat(spider.dataset.vy);

            x += vx * (dt / 1000);
            y += vy * (dt / 1000);

            // Bounce
            if (x < 25 || x > this.container.clientWidth - 25) {
                vx *= -1;
                x = Math.max(25, Math.min(this.container.clientWidth - 25, x));
            }
            if (y < 25 || y > this.container.clientHeight - 25) {
                vy *= -1;
                y = Math.max(25, Math.min(this.container.clientHeight - 25, y));
            }

            // Random jitter
            if (Math.random() < 0.05) {
                vx = (Math.random() - 0.5) * 300;
                vy = (Math.random() - 0.5) * 300;
            }

            spider.style.left = `${x}px`;
            spider.style.top = `${y}px`;
            spider.dataset.vx = vx;
            spider.dataset.vy = vy;

            // Rotate
            const angle = Math.atan2(vy, vx);
            spider.style.transform = `translate(-50%, -50%) rotate(${angle}rad)`;
        });
    }

    onDown(e) {
        if (!this.active || this.won) return;

        this.spiders.forEach(spider => {
            if (spider.dataset.alive !== 'true') return;

            const rect = spider.getBoundingClientRect();
            const hitMargin = 20;

            if (
                e.clientX >= rect.left - hitMargin &&
                e.clientX <= rect.right + hitMargin &&
                e.clientY >= rect.top - hitMargin &&
                e.clientY <= rect.bottom + hitMargin
            ) {
                this.squash(spider);
            }
        });
    }

    squash(spider) {
        spider.dataset.alive = 'false';
        spider.style.transition = 'transform 0.1s';
        spider.style.transform += ' scale(1.5, 0.5)';
        spider.style.background = this.primaryColor;
        spider.style.boxShadow = `0 0 20px ${this.primaryColor}`;

        // Change legs color
        Array.from(spider.children).forEach(leg => leg.style.background = this.primaryColor);

        this.squashedCount++;
        if (this.squashedCount >= this.spiderCount) {
            this.win();
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
    }
}
